/**
 * 트랜잭션 처리 유틸리티
 * Firebase Realtime Database의 원자적 업데이트를 보장
 */

import { ref, get, update, runTransaction } from 'firebase/database';
import { database } from '../config/firebase';
import { DEFAULT_GROUP_SETTINGS } from '../config/accountSyncConfig';

export interface TransactionOperation {
  path: string;
  value: any;
  type: 'set' | 'update' | 'delete';
}

export class AccountSyncTransaction {
  private operations: TransactionOperation[] = [];
  private rollbackData: Map<string, any> = new Map();

  /**
   * 작업 추가
   */
  addOperation(path: string, value: any, type: 'set' | 'update' | 'delete' = 'set'): void {
    this.operations.push({ path, value, type });
  }

  /**
   * 트랜잭션 실행
   */
  async execute(): Promise<void> {
    if (this.operations.length === 0) {
      return;
    }

    try {
      // 롤백을 위한 현재 데이터 백업
      await this.backupCurrentData();

      // 모든 작업을 하나의 업데이트로 실행
      const updates: { [key: string]: any } = {};
      
      for (const op of this.operations) {
        if (op.type === 'delete') {
          updates[op.path] = null;
        } else {
          updates[op.path] = op.value;
        }
      }

      // 원자적 업데이트 실행
      await update(ref(database), updates);
      
      // 성공 시 초기화
      this.clear();
    } catch (error) {
      // 실패 시 롤백
      await this.rollback();
      throw error;
    }
  }

  /**
   * 현재 데이터 백업
   */
  private async backupCurrentData(): Promise<void> {
    for (const op of this.operations) {
      const dataRef = ref(database, op.path);
      const snapshot = await get(dataRef);
      this.rollbackData.set(op.path, snapshot.exists() ? snapshot.val() : null);
    }
  }

  /**
   * 롤백 실행
   */
  private async rollback(): Promise<void> {
    console.error('트랜잭션 실패, 롤백 시작...');
    
    const rollbackUpdates: { [key: string]: any } = {};
    
    for (const [path, originalValue] of this.rollbackData.entries()) {
      rollbackUpdates[path] = originalValue;
    }

    try {
      await update(ref(database), rollbackUpdates);
      console.log('롤백 완료');
    } catch (rollbackError) {
      console.error('롤백 실패:', rollbackError);
      throw new Error('트랜잭션 롤백 실패. 데이터 무결성을 확인하세요.');
    }
  }

  /**
   * 트랜잭션 초기화
   */
  clear(): void {
    this.operations = [];
    this.rollbackData.clear();
  }

  /**
   * 재시도 로직이 포함된 트랜잭션 실행
   */
  async executeWithRetry(maxRetries: number = DEFAULT_GROUP_SETTINGS.RETRY_ATTEMPTS): Promise<void> {
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await this.execute();
        return; // 성공
      } catch (error: any) {
        lastError = error;
        console.warn(`트랜잭션 시도 ${attempt}/${maxRetries} 실패:`, error.message);
        
        if (attempt < maxRetries) {
          // 지수 백오프로 재시도 지연
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError || new Error('트랜잭션 실행 실패');
  }
}

/**
 * 배치 업데이트 헬퍼 함수
 */
export async function batchUpdate(updates: { [key: string]: any }): Promise<void> {
  const transaction = new AccountSyncTransaction();
  
  for (const [path, value] of Object.entries(updates)) {
    transaction.addOperation(
      path,
      value,
      value === null ? 'delete' : 'set'
    );
  }
  
  await transaction.executeWithRetry();
}

/**
 * 안전한 트랜잭션 실행 래퍼
 */
export async function safeTransaction<T>(
  operation: () => Promise<T>,
  rollbackOperation?: () => Promise<void>
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (rollbackOperation) {
      try {
        await rollbackOperation();
      } catch (rollbackError) {
        console.error('롤백 실패:', rollbackError);
      }
    }
    throw error;
  }
}

/**
 * Firebase 트랜잭션을 사용한 카운터 업데이트
 * (그룹 멤버 수 등의 카운터 업데이트에 사용)
 */
export async function updateCounter(
  path: string,
  delta: number
): Promise<number> {
  const counterRef = ref(database, path);
  
  const result = await runTransaction(counterRef, (currentValue) => {
    if (currentValue === null) {
      return delta > 0 ? delta : 0;
    }
    
    const newValue = currentValue + delta;
    return newValue >= 0 ? newValue : 0;
  });

  if (!result.committed) {
    throw new Error('카운터 업데이트 실패');
  }

  return result.snapshot.val() || 0;
}

export default AccountSyncTransaction;